<script lang="ts" setup>
import { computed, ref, toRefs } from 'vue';
import { useMessageCenterStore } from '@/store';
import ConfirmAction from '@/components/ConfirmAction';
import DisplayChatName from './../../DisplayChatName';
import { can, useDateTime } from '@/composables';
import { PERMISSIONS } from '@/enum';

interface PropsInterface {
  id: number;
  name: string;
  lastMessage?: string;
  unreadCount: number;
  updatedAt: string;
}

const messageCenter = useMessageCenterStore();

const props = defineProps<PropsInterface>();
const { id, name, lastMessage, unreadCount, updatedAt } = toRefs(props);
const toggleDeleteConversation = ref(false);

const onOpenChat = () => {
  if (can([PERMISSIONS.CHAT_SHOW])) messageCenter.setActiveChat(id.value);
};

const onRemoveChat = () => {
  messageCenter.deleteChat(id.value);
};

const getChatAvatar = () => {
  const words = name.value.trim().split(/\s+/);
  const initials = words.map((word) => word[0].toUpperCase()).join('');
  return initials.length > 2 ? initials.slice(0, 2) : initials;
};

const menuItems = [
  {
    label: 'Delete Conversation',
    command: () => {
      toggleDeleteConversation.value = true;
    },
    visible: can([PERMISSIONS.CHAT_DESTROY]),
  },
];

const showMenuItems = computed(() => {
  return menuItems.filter((menu) => menu.visible === true).length > 0;
});
</script>

<template>
  <div
    class="surface-100 border-round-lg flex justify-content-between align-items-center my-2 p-2 w-full transition-duration-100 cursor-pointer hover:surface-50"
    @click="onOpenChat"
  >
    <div
      class="user-avatar inline-flex align-items-center justify-content-center text-white text-lg font-bold mr-2 cursor-pointer"
    >
      {{ getChatAvatar() }}
    </div>
    <div class="flex-grow-1">
      <div class="flex justify-content-between justify-content-start mb-1">
        <DisplayChatName :name="name" class="text-sm max-w-12rem" />
        <ActionMenu
          v-if="showMenuItems"
          :menu-items="menuItems"
          icon="more_horiz"
          class="text-dark-medium h-1rem flex align-items-center"
        />
      </div>
      <div class="flex justify-content-between">
        <p class="max-w-7rem overflow-hidden text-dark text-overflow-ellipsis white-space-nowrap">
          {{ lastMessage }}
        </p>
        <div class="flex align-content-center">
          <Icon v-if="unreadCount > 0" name="circle" class="material-icons text-teal-300 text-sm unread-icon" />
          <span class="text-sm">{{ useDateTime().formatDateTimeForMessageFromUTC(updatedAt) }}</span>
        </div>
      </div>
    </div>
  </div>
  <ConfirmAction
    v-if="toggleDeleteConversation && can([PERMISSIONS.CHAT_DESTROY])"
    header="Delete Conversation"
    accept-label="Yes"
    reject-label="No"
    @accept="onRemoveChat()"
    @cancel="toggleDeleteConversation = false"
  >
    <div>Are you sure want to delete this conversation?</div>
  </ConfirmAction>
</template>

<style lang="scss" scoped>
.profile-container {
  width: 30px;
  height: 60px;
}

.p-10-8 {
  padding: 10px 8px;
}
.text-dark {
  color: var(--surface-900);
}
.text-dark-medium {
  color: var(--gray-700);
}
.unread-icon {
  padding-top: 2px;
}
.user-avatar {
  width: 40px;
  height: 40px;
  background-color: var(--primary-color-medium);
  border-radius: 50%;
  transition: 0.2s ease;

  &:hover {
    opacity: 0.75;
  }
}
</style>
